import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { auth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import "./../App.css";
import { useNavigate } from "react-router-dom";

// Shared Base Styles for Navigation Links
const LINK_STYLE = {
    color: "#fff",
    fontWeight: "500",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px 15px",
    borderRadius: "8px",
    transition: "transform 0.3s ease, background 0.3s ease",
    textDecoration: "none",
    borderBottom: "1px solid rgba(245, 238, 238, 0.1)",
    cursor: "pointer",
    position: "relative"
};

const LINK_HOVER = {
    background: "rgba(255,255,255,0.15)",
    translateX: 6
};

// Recursive Menu Item Component to allow infinite Nesting/Submenus
function MenuItemNode({ item, level = 0, navigate }) {
    const [isOpen, setIsOpen] = useState(false);
    const hasSubmenu = item.submenu && item.submenu.length > 0;

    // Dynamically increase padding left based on depth level
    const calculatedStyle = {
        ...LINK_STYLE,
        paddingLeft: `${15 + level * 15}px`,
        background: level > 0 ? `rgba(255, 255, 255, ${0.04 * level})` : "transparent"
    };

    const handleNodeClick = (e) => {
        if (hasSubmenu) {
            e.preventDefault();
            setIsOpen(!isOpen);
        } else if (item.link) {
            navigate(item.link);
        }
    };

    return (
        <div style={{ width: "100%" }}>
            <motion.div
                onClick={handleNodeClick}
                style={calculatedStyle}
                whileHover={LINK_HOVER}
            >
                {/* Render icon if provided, otherwise generic nested bulletin dot */}
                {item.icon ? (
                    <i className={item.icon} style={{ fontSize: "18px", width: "30px", textAlign: "center" }}></i>
                ) : (
                    <span style={{ width: "30px", textAlign: "center", fontSize: "14px", opacity: 0.7 }}>•</span>
                )}
                
                <span style={{ fontSize: "14px" }}>{item.text}</span>

                {hasSubmenu && (
                    <i
                        className="fas fa-chevron-right"
                        style={{
                            marginLeft: "auto",
                            transition: "transform 0.3s ease",
                            fontSize: "12px",
                            transform: isOpen ? "rotate(90deg)" : "rotate(0deg)"
                        }}
                    ></i>
                )}
            </motion.div>

            {/* Submenu Drawer Block */}
            {hasSubmenu && isOpen && (
                <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25, ease: "easeInOut" }}
                    style={{ overflow: "hidden" }}
                >
                    {item.submenu.map((subItem, index) => (
                        <MenuItemNode 
                            key={index} 
                            item={subItem} 
                            level={level + 1} 
                            navigate={navigate} 
                        />
                    ))}
                </motion.div>
            )}
        </div>
    );
}

function Menu() {
    const navigate = useNavigate();
    const [username, setUsername] = useState("Guest");
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);

    // Flattened structure removing the outer "Payroll Mgmt" parent category
    const fullMenu = [
        { icon: "fas fa-home", text: "Dashboard", link: "/home" },
        { icon: "fas fa-users", text: "Staff Profiles", link: "/users-mgmt" },
        // { icon: "fas fa-hand-holding-usd", text: "Payment Allocation", link: "/payment-allocation" },
        // {
        //     icon: "fas fa-file-invoice-dollar",
        //     text: "Payslip Processing",
        //     submenu: [
        //         { text: "Payslip Generation", link: "/payslips" },
        //     ]
        // },
        { icon: "fas fa-calendar-alt", text: "Staff Attendance", link: "/attendance" },
        {
            icon: "fas fa-file",
            text: "Report",
            submenu: [
                { text: "Attendance Report", link: "/attendance-report" },
                // { text: "Monthly Payments", link: "/monthly-payment" }
            ]
        }
    ];

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            setUserData(user);
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        const fetchUserRole = async () => {
            try {
                if (!userData) return;
                const userRef = doc(db, "authroles", userData.uid);
                const userSnap = await getDoc(userRef);

                if (userSnap.exists()) {
                    const user = userSnap.data();
                    setUsername(user.name || "Guest");
                }
                setLoading(false);
            } catch (error) {
                console.error("Error fetching user role:", error);
            }
        };
        fetchUserRole();
    }, [userData]);

    const firstLetter = username.charAt(0).toUpperCase();

    return (
        <aside
            className="main-sidebar"
            style={{
                background: "linear-gradient(180deg, #096065 0%, #126668 50%, #08456a 100%)",
                backgroundBlendMode: "soft-light",
                backgroundSize: "cover",
                color: "#fff",
                height: "100vh",
                width: "260px",
                position: "fixed",
                top: 0,
                left: 0,
                overflow: "hidden",
                boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
                display: "flex",
                flexDirection: "column"
            }}
        >
            {/* TOP PROFILE BAR */}
            <div
                style={{
                    textAlign: "center",
                    borderBottom: "1px solid rgba(255,255,255,0.2)",
                    padding: "15px 10px",
                    position: "sticky",
                    top: 0,
                    background: "rgba(1, 40, 43, 0.8)",
                    backdropFilter: "blur(6px)",
                    zIndex: 10,
                }}
            >
                <div
                    style={{
                        width: "60px",
                        height: "60px",
                        borderRadius: "50%",
                        backgroundColor: "rgba(255,255,255,0.2)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "28px",
                        fontWeight: "700",
                        margin: "0 auto 10px auto",
                        color: "#fff",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.3)"
                    }}
                >
                    {firstLetter}
                </div>
                <h5 style={{ fontWeight: "600", margin: 0 }}>{username}</h5>
            </div>

            {/* SCROLLABLE MENU AREA */}
            <div
                className="sidebar-scroll"
                style={{
                    padding: "10px 8px",
                    overflowY: "auto",
                    overflowX: "hidden",
                    flex: 1,
                    paddingBottom: "40px"
                }}
            >
                {fullMenu.map((item, index) => (
                    <MenuItemNode 
                        key={index} 
                        item={item} 
                        level={0} 
                        navigate={navigate} 
                    />
                ))}
            </div>
        </aside>
    );
}

export default Menu;